import api from "@/lib/api";
import { reportPaymentError, showWithSheetTimeout } from "@/hooks/useTokenPurchase";
import { trackEvent } from "@/utils/analytics";

// Mirrors useTokenPurchase.js exactly (sheet opened synchronously on the click for transient
// activation, visibility-aware timeout that never aborts a buyer already inside the native
// sheet, late-capture backstop) - the only difference is the SKU points at a Google Play
// SUBSCRIPTION product instead of a consumable token pack.
function openPlaySubscriptionSheet(sku, label, onLateResult) {
  const request = new window.PaymentRequest(
    [{ supportedMethods: "https://play.google.com/billing", data: { sku } }],
    { total: { label, amount: { currency: "EUR", value: "0" } } },
  );
  return showWithSheetTimeout(request, onLateResult);
}

// Same policy as token packs (17/08/2026 RCA): 1 abort = probably a voluntary cancel (silent
// but reported), 2 consecutive aborts = the billing channel is broken on this device -> the
// Mollie card fallback is offered instead of a dead end.
let consecutiveSubAborts = 0;

// Bascule web automatique dès le 1er échec Google Play (15/08/2026) : crée l'abonnement Mollie
// et redirige directement vers la page de paiement hébergée (carte/Google Pay/Apple Pay).
async function autoWebSubFallback(plan) {
  try {
    // promo_code transmis pour que le 1er mois Mollie garde la réduction (ex: SUMMER2026 -50%),
    // exactement comme le flux web normal - bug signalé le 16/08/2026 (19,99€ au lieu de 9,99€).
    const { data } = await api.post("/subscriptions/create", {
      plan_id: plan.plan_id, platform: "web", promo_code: plan.promo_code || null,
    });
    if (data?.checkout_url) {
      trackEvent("mollie_checkout_redirect", { value: plan.price_eur, currency: "EUR", reason: "play_billing_auto_fallback" });
      window.location.assign(data.checkout_url);
      return { ...data, redirected: true };
    }
  } catch { /* noop - embedded card form fallback below */ }
  return null;
}

export async function subscribeViaGooglePlay(plan) {
  trackEvent("begin_checkout", { value: plan.price_eur, currency: "EUR", method: "google_play_subscription" });
  const shownAt = Date.now();
  const sheetPromise = openPlaySubscriptionSheet(plan.sku, plan.name, async (lateResponse) => {
    // Payment completed AFTER the timeout already gave up - save the sale anyway: capture the
    // token, verify + acknowledge server-side (releases Google's 3-day authorization hold).
    try {
      const token = lateResponse.details?.purchaseToken;
      await lateResponse.complete("success");
      const { data: pending } = await pendingPromise;
      if (token) {
        await api.post("/subscriptions/google-play/verify", { subscription_id: pending.subscription_id, purchase_token: token });
        reportPaymentError({
          payment_id: pending.subscription_id, provider: "google_play", step: "subscription.late_capture",
          error_name: "LateSuccess", error_message: "subscription completed after timeout - token captured and verified",
        });
      }
    } catch { /* noop - restorePlaySubscriptions() will catch it on next app launch */ }
  });
  const pendingPromise = api.post("/subscriptions/create", { plan_id: plan.plan_id, platform: "android" });

  let response;
  try {
    response = await sheetPromise;
  } catch (err) {
    let createErr = null;
    const pending = await pendingPromise.catch((e) => {
      createErr = e;
      return null;
    });
    // The sheet closing (RESULT_CANCELED) is sometimes just the visible consequence of the
    // server refusing the creation (409 "already subscribed") - surface THAT reason as a clear
    // toast instead of treating it like a silent voluntary cancel (18/08/2026 prod analysis).
    if (createErr?.response?.status === 409) {
      throw new Error(createErr.response?.data?.detail || "Vous avez déjà un abonnement actif");
    }
    if (err?.name === "AbortError") {
      const elapsedMs = Date.now() - shownAt;
      consecutiveSubAborts += 1;
      trackEvent("checkout_abandoned", { value: plan.price_eur, currency: "EUR", method: "google_play_subscription" });
      reportPaymentError({
        payment_id: pending?.data?.subscription_id, provider: "google_play", step: "subscription.request.show",
        error_name: "AbortError", error_message: `${err?.message || "sheet dismissed"} (consecutive #${consecutiveSubAborts})`,
        elapsed_ms: elapsedMs, sku: plan.sku,
      });
      // Bascule web au 2e échec consécutif seulement (règle admin 18/08/2026) ; fermeture <3s
      // = panne technique -> bascule immédiate. 1er échec -> toast "réessayez".
      if (elapsedMs < 3000 || consecutiveSubAborts >= 2) {
        const web = await autoWebSubFallback(plan);
        if (web) return web;
        const fallbackErr = new Error("Le paiement Google Play a échoué.");
        fallbackErr.playBillingFallback = true;
        throw fallbackErr;
      }
      const cancelled = new Error("cancelled");
      cancelled.isPurchaseCancelled = true;
      throw cancelled;
    }
    reportPaymentError({
      payment_id: pending?.data?.subscription_id, provider: "google_play", step: "subscription.request.show",
      error_name: err?.name, error_message: err?.message, elapsed_ms: Date.now() - shownAt, sku: plan.sku,
    });
    const web = await autoWebSubFallback(plan);
    if (web) return web;
    const fallbackErr = new Error("Le paiement Google Play a échoué.");
    fallbackErr.playBillingFallback = true;
    throw fallbackErr;
  }

  consecutiveSubAborts = 0;
  const { data: pending } = await pendingPromise;
  const purchaseToken = response.details?.purchaseToken;
  await response.complete("success");
  if (!purchaseToken) {
    reportPaymentError({
      payment_id: pending.subscription_id, provider: "google_play", step: "subscription.purchaseToken",
      error_name: "MissingToken", error_message: "missing purchaseToken in response.details",
    });
    throw new Error("Jeton d'achat introuvable dans la réponse Google Play, veuillez réessayer.");
  }

  trackEvent("add_payment_info", { value: plan.price_eur, currency: "EUR", method: "google_play_subscription" });
  try {
    const { data } = await api.post("/subscriptions/google-play/verify", {
      subscription_id: pending.subscription_id, purchase_token: purchaseToken,
    });
    return data;
  } catch (err) {
    reportPaymentError({
      payment_id: pending.subscription_id, provider: "google_play", step: "subscription.verify",
      error_name: err?.name, error_message: err?.response?.data?.detail || err?.message,
    });
    throw err;
  }
}


// Rescue/re-sync on app launch (TWA only): re-submits any Google Play subscription purchase
// whose server verification failed at purchase time, BEFORE Google's ~3-day auto-refund of
// unacknowledged purchases. Silent no-op everywhere else.
export async function restorePlaySubscriptions() {
  try {
    if (!window.getDigitalGoodsService) return null;
    const service = await window.getDigitalGoodsService("https://play.google.com/billing");
    if (!service) return null;
    const purchases = await service.listPurchases();
    const subs = (purchases || []).filter((p) => (p.itemId || "").startsWith("sub_"));
    if (!subs.length) return null;
    const { data } = await api.post("/subscriptions/google-play/restore", {
      purchases: subs.map((p) => ({ sku: p.itemId, purchase_token: p.purchaseToken })),
    });
    return data;
  } catch {
    return null;
  }
}
